import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  savePermissions,
  selectPermissionsSaving,
  ALL_MODULES,
} from "../../store/slices/permissionSlice";
import RoleApi from "../../api/RoleApi";
import { showError, showSuccess } from "../../utils/toastUtils";

// Local permission state shape: { dashboard: { canRead, canWrite, canDelete }, ... }
const buildEmptyPerms = () =>
  ALL_MODULES.reduce((acc, { key }) => {
    acc[key] = { canRead: false, canWrite: false, canDelete: false };
    return acc;
  }, {});

function AddDetails({ refreshList }) {
  const dispatch = useAppDispatch();
  const saving = useAppSelector(selectPermissionsSaving);

  const [role, setRole] = useState("");
  const [roleLoading, setRoleLoading] = useState(false);
  const [permissions, setPermissions] = useState(buildEmptyPerms);

  const handleToggle = (module, permKey) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permKey]: !prev[module][permKey],
      },
    }));
  };

  const resetForm = () => {
    setRole("");
    setPermissions(buildEmptyPerms());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!role.trim()) return showError("Role name is required");

    try {
      setRoleLoading(true);

      // Step 1 — Create the role
      const roleRes = await RoleApi.add({ role: role.trim(), type: "employee" });

      if (roleRes.code !== 200) {
        showError(roleRes.message || "Failed to create role");
        return;
      }

      const newRoleId = roleRes.data?._id;

      if (!newRoleId) {
        showError("Role created but ID not returned");
        return;
      }

      // Step 2 — Bulk-set permissions for the new role
      const result = await dispatch(
        savePermissions({ roleId: newRoleId, permissions })
      );

      if (savePermissions.fulfilled.match(result)) {
        showSuccess("Role created with permissions");
        refreshList?.();
        resetForm();
        document.getElementById("closeOffcanvasBtn")?.click();
      } else {
        showError(result.payload || "Role created but failed to save permissions");
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Something went wrong");
    } finally {
      setRoleLoading(false);
    }
  };

  const isSubmitting = roleLoading || saving;

  return (
    <>
      <div className="offcanvas-header border-bottom">
        <h5 className="fw-semibold">Add New Role</h5>
        <button
          type="button"
          className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
          data-bs-dismiss="offcanvas"
          id="closeOffcanvasBtn"
          aria-label="Close"
        >
          <i className="ti ti-x" />
        </button>
      </div>

      <div className="offcanvas-body">
        <form onSubmit={handleSubmit}>
          {/* Role Name */}
          <div className="mb-3">
            <label className="form-label">
              Role Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. accountant"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>

          {/* Permissions Table */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Permissions</label>
            <div className="table-responsive">
              <table className="table table-bordered table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Module</th>
                    <th className="text-center">Read</th>
                    <th className="text-center">Write</th>
                    <th className="text-center">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_MODULES.map(({ key, label }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      {["canRead", "canWrite", "canDelete"].map((permKey) => (
                        <td key={permKey} className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!permissions[key][permKey]}
                            onChange={() => handleToggle(key, permKey)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving...
              </>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
    </>
  );
}

export default AddDetails;
